import type { EntryOrganizationRow, EntryPhase } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { NocFlag } from "../common/noc-flag";
import { MedalCount } from "../common/medal-icon";
import { StatePill, WindowLine } from "./parts";
import "./overview.css";

/**
 * The NOCs of one phase as cards: flag · name · code · state, then THIS
 * phase's three numbers only, big — EOI: sports, estimated athletes (men |
 * women), expected medals as gold / silver / bronze medals; EBN: events,
 * entries, athletes implied — and the window (or when it was submitted)
 * with Open. The whole card is the link (Open's hit area stretches over it).
 * A NOC that submitted wears the green "done" frame; one with nothing saved
 * is a quiet card with one line instead of a row of zeros, so the NOCs with
 * numbers stand out. Every card is the same size; the foot sits at the bottom.
 *
 * Pure: the rows are one page of `GET /entries/organizations`, already
 * searched and sorted by the API.
 */
export function NocCards({
	rows,
	phase,
	gamesId,
	loading,
}: {
	rows: readonly EntryOrganizationRow[];
	phase: EntryPhase;
	gamesId: string | undefined;
	loading: boolean;
}) {
	const { t } = useTranslation("entries");
	return (
		<ul className="ent-nocs" aria-label={t("overview.nocs")} data-busy={loading || undefined}>
			{rows.map((r) => (
				<NocCard key={r.organization.id} row={r} phase={phase} gamesId={gamesId} />
			))}
		</ul>
	);
}

/** Placeholder cards while the first page loads. */
export function NocCardsSkeleton({ count = 8 }: { count?: number }) {
	return (
		<div className="ent-nocs" aria-busy>
			{Array.from({ length: count }, (_, i) => (
				<div key={i} className="skeleton h-[9.5rem] rounded-[1rem]" />
			))}
		</div>
	);
}

function NocCard({ row: r, phase, gamesId }: { row: EntryOrganizationRow; phase: EntryPhase; gamesId: string | undefined }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const p = phase === "EOI" ? r.eoi : r.ebn;
	const done = p.state === "FINAL";
	const idle = p.state === "NOT_STARTED";
	const route = phase === "EOI" ? "/accreditation/entries/eoi" : "/accreditation/entries/ebn";
	const org = r.organization;
	const athletes = r.eoi.est_athletes_men + r.eoi.est_athletes_women;
	const medals = { g: fmt(r.eoi.expected_gold), s: fmt(r.eoi.expected_silver), b: fmt(r.eoi.expected_bronze) };

	return (
		<li className="ent-noc" data-done={done || undefined} data-idle={idle || undefined} data-state={p.state}>
			<header className="ent-noc__head">
				<NocFlag code={org.code} size="lg" />
				<span className="ent-noc__name">
					<b title={org.name}>{org.name}</b>
					{org.code && <span className="ent-noc__code">{org.code}</span>}
				</span>
				<span className="ent-noc__state">
					<StatePill state={p.state} />
				</span>
			</header>

			{idle ? (
				<p className="ent-noc__idle">{t(`overview.cards.notStarted.${phase}`)}</p>
			) : phase === "EOI" ? (
				<dl className="ent-noc__stats">
					<Stat label={t("overview.table.sports")}>{fmt(r.eoi.sports)}</Stat>
					<Stat label={t("overview.table.estAthletes")} hint={t("overview.cards.splitAria", { m: fmt(r.eoi.est_athletes_men), w: fmt(r.eoi.est_athletes_women) })}>
						{fmt(athletes)}
					</Stat>
					<Stat label={t("overview.table.medals")} hint={t("overview.cards.medalsAria", medals)}>
						<span className="ent-noc__medals" aria-hidden>
							<MedalCount medal="gold" value={r.eoi.expected_gold} />
							<MedalCount medal="silver" value={r.eoi.expected_silver} />
							<MedalCount medal="bronze" value={r.eoi.expected_bronze} />
						</span>
					</Stat>
				</dl>
			) : (
				<dl className="ent-noc__stats">
					<Stat label={t("overview.table.events")}>{fmt(r.ebn.events)}</Stat>
					<Stat label={t("overview.table.entries")}>{fmt(r.ebn.entries)}</Stat>
					<Stat label={t("overview.table.implied")}>{fmt(r.ebn.implied_athletes)}</Stat>
				</dl>
			)}

			<footer className="ent-noc__foot">
				<span className="ent-noc__when">
					{done && p.submitted_at ? (
						<span title={formatDateTime(p.submitted_at)}>{t("overview.cards.submittedAt", { at: formatShort(p.submitted_at) })}</span>
					) : (
						<WindowLine window={p.window} />
					)}
				</span>
				<Link to={route} search={{ games_id: gamesId, org: org.id }} className="btn btn-sm btn-line ent-noc__open" aria-label={t("overview.table.openLabel", { org: org.name })}>
					{t("overview.table.open")}
					<svg viewBox="0 0 16 16" aria-hidden="true" className="ent-noc__arrow size-[0.875rem]">
						<path d="M3 8h9m-3.5-3.5L12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</Link>
			</footer>
		</li>
	);
}

/** One number with its label; `hint` is the accessible name and tooltip when the number alone is not the whole story. */
function Stat({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
	return (
		<div className="ent-noc__stat" title={hint}>
			<dt>{label}</dt>
			<dd aria-label={hint}>{children}</dd>
		</div>
	);
}
